import {addAndRunAnalysisSteps} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {
  AnalysisRightSidebarItem,
  AnalysisRightSidebarItemType,
} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  DataReference,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import {DataFrameColumns} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {VISUALIZATION_TYPE_TO_READABLE} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import {getDefaultVisualizationNameForVisualizationType} from 'insights/analytics-visualizations/getDefaultVisualizationNameForVisualizationType';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {withLoadingScreen} from 'util/Promises';

import {ChartOptionType} from './charting/ChartingTypes';
import {getDefaultChartConfig} from './charting/ChartingUtils';
import {getPersistedChartViewConfig} from './charting/getPersistedVisualizationConfig';

function getStepConfig(
  chartOptionType: ChartOptionType,
  dataFrameColumns: DataFrameColumns,
  initialVisualizationConfig?: VisualizationConfig
): PipelineStepConfig<PipelineStepType.CHART_VIEW, 0> {
  return initialVisualizationConfig != null
    ? {
        // TODO(BNCH-119690): Unify box plot and VisualizationType path
        chartConfig: getPersistedChartViewConfig(initialVisualizationConfig),
        chartName: getDefaultVisualizationNameForVisualizationType(
          initialVisualizationConfig.parameters.__typename
        ),
      }
    : getDefaultChartConfig(chartOptionType, dataFrameColumns);
}
/** @package */
export async function createViewAndChartViewSteps(
  analysis: AnalysisGraphQL,
  inputDataFrameId: string,
  inputDataReference: DataReference,
  chartOptionType: ChartOptionType,
  dataFrameColumns: DataFrameColumns,
  initialVisualizationConfig?: VisualizationConfig
): Promise<Array<GetAnalysisPipelineStep>> {
  return await addAndRunAnalysisSteps(analysis.id, [
    {
      stepType: PipelineStepType.DATASET_VIEW,
      stepVersion: 0,
      stepConfig: {},
      stepInputParents: {
        [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [inputDataReference],
      },
      stepInput: {inputDataFrameId},
      runStep: true,
    },
    {
      stepType: PipelineStepType.CHART_VIEW,
      stepVersion: 0,
      stepConfig: getStepConfig(chartOptionType, dataFrameColumns, initialVisualizationConfig),
      stepInputParents: {
        [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [{stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME}],
      },
      runStep: true,
    },
  ]);
}

/** @package */
export async function createViewAndTableViewSteps(
  analysis: AnalysisGraphQL,
  inputDataFrameId: string,
  inputDataReference: DataReference
): Promise<Array<GetAnalysisPipelineStep>> {
  return await addAndRunAnalysisSteps(analysis.id, [
    {
      stepType: PipelineStepType.DATASET_VIEW,
      stepVersion: 0,
      stepConfig: {},
      stepInputParents: {
        [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [inputDataReference],
      },
      stepInput: {inputDataFrameId},
      runStep: true,
    },
    {
      stepType: PipelineStepType.TABLE_VIEW,
      stepVersion: 0,
      stepConfig: {
        viewName: VISUALIZATION_TYPE_TO_READABLE[VisualizationType.TABLE],
      },
      stepInputParents: {
        [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [{stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME}],
      },
      runStep: true,
    },
  ]);
}

export interface CreateViewAndOpenChartOrTableViewParams {
  analysis: AnalysisGraphQL;
  inputDataFrameId: string;
  inputDataReference: DataReference;
  onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void;
  onChangeSelectedRightSidebarItem: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  initialVisualizationConfig?: VisualizationConfig;
  chartOptionType: ChartOptionType;
  dataFrameColumns: DataFrameColumns;
}

export default async function createViewAndOpenChartOrTableView({
  analysis,
  inputDataFrameId,
  inputDataReference,
  onOpenStepInitializationView,
  onChangeSelectedRightSidebarItem,
  initialVisualizationConfig,
  dataFrameColumns,
  chartOptionType,
}: CreateViewAndOpenChartOrTableViewParams): Promise<void> {
  await withLoadingScreen<void>(
    (async () => {
      const newSteps =
        initialVisualizationConfig == null ||
        getVisualizationType(initialVisualizationConfig) !== VisualizationType.TABLE
          ? await createViewAndChartViewSteps(
              analysis,
              inputDataFrameId,
              inputDataReference,
              chartOptionType,
              dataFrameColumns,
              initialVisualizationConfig
            )
          : await createViewAndTableViewSteps(analysis, inputDataFrameId, inputDataReference);

      const viewReference = {
        stepId: newSteps[newSteps.length - 1].id,
        outputReference: {
          stepDataName:
            newSteps[newSteps.length - 1].definition.stepType === PipelineStepType.CHART_VIEW
              ? SINGLE_CHART_OUTPUT_STEP_DATA_NAME
              : SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
          rawDataIndex: 0,
        },
      };
      onOpenStepInitializationView({
        type: AnalysisMainViewItemType.DATA_VIEW,
        viewReference,
      });
      onChangeSelectedRightSidebarItem({
        type: AnalysisRightSidebarItemType.EDIT_VIEW,
        viewReference,
      });
    })(),
    'Creating view...'
  );
}
